import React, { useState, useMemo, useEffect } from 'react';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { actions as adminsActions, selectors as adminsSelectors, UpdateAdminPayload } from 'store/admins';
import { selectors as venuesSelectors, actions as venuesActions, Venue } from 'store/venues';
import { selectors as authSelectors } from 'store/auth';
import {
  MuiBox,
  Box,
  Button,
  Typography,
  Checkbox,
  Card,
  InputField,
  Grid,
  SelectDropdown,
  color
} from '@kis-technologies/kis-ui';
import { MAINTENANCE, INVALID, VALID } from '../constants';
import { UpdateUserFormProps, RoleItem } from '../types';
import {
  getHasInitialInput,
  getHasInvalidInput,
  getHasEmptyInput,
  getInitialForm,
  validationSchema,
  defaultRoles,
  globalRole,
  fullRoleList
} from '../utils';
import { getSortedList } from 'utils/stringFormat';
import { ValidPasswordToolTip } from '../../ValidPasswordToolTip';
import { useStyles } from '../styled';

export const UpdateUserForm = (props: UpdateUserFormProps) => {
  const { admin } = props;

  // ACTION CREATORS AND SELECTORS
  const [updateAdmin, getValidPinCode, resetPinCode, resetError] = useActions([
    adminsActions.updateAdmin,
    adminsActions.getValidPinCode,
    adminsActions.resetPinCode,
    adminsActions.resetError
  ]);
  const getVenues = useActions(venuesActions.getVenues);
  const venues = useSelector(venuesSelectors.selectVenues);
  const authAdmin = useSelector(authSelectors.selectAdmin);
  const validPincode = useSelector(adminsSelectors.selectPinCode);
  const { error } = useSelector(adminsSelectors.selectAction);
  const classes = useStyles();

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getInitialForm(admin);
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [showPasswordInfo, setPasswordInfoVisibility] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue>(venues[0]);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(
    fullRoleList.find((r) => r.code === admin.role) || null
  );

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form, admin), [form, admin]);
  const hasInitialInput = useMemo(() => getHasInitialInput(form, admin), [form, admin]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(form, validation), [form, validation]);

  useEffect(() => {
    if (!venues || !venues.length) {
      getVenues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (venues && venues.length) {
      if (admin.venueId) {
        const venueIndex = venues.findIndex((venue) => venue.id === admin.venueId);
        setSelectedVenue(venues[venueIndex]);
      } else {
        setSelectedVenue(venues[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues]);

  React.useEffect(() => {
    let tempRoles: RoleItem[] = [];
    if (authAdmin && authAdmin.role === 'GlobalAdmin') {
      tempRoles = globalRole;
    }
    tempRoles = tempRoles.concat(defaultRoles);
    setRoles(tempRoles);
  }, [authAdmin]);

  React.useEffect(() => {
    if (validPincode) {
      setForm({ ...form, pinCode: validPincode });
    }

    return () => resetPinCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validPincode]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // SEND ALERTS CHECKBOX CLICK
  const onAlertCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (error) resetError();

    // SETS FORM MAINTENANCE OR CLIENT ADMIN CHECKBOXES
    setForm({ ...form, sendAlertEmails: !form.sendAlertEmails });
  };

  const handleRoleChange = (role: RoleItem) => {
    if (error) resetError();
    setSelectedRole(role);
    setForm({ ...form, role: role.code });
  };

  // FORM INPUT CHANGE
  const onInputChange = (key: keyof ReturnType<typeof getInitialForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (error) resetError();

    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }
    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });

    if (key === 'password') {
      setPasswordInfoVisibility(true);
    }
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });
    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await validationSchema.validate({ [key]: value && value.toString().trim() }).catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });

    if (key === 'password') {
      setPasswordInfoVisibility(false);
    }
  };

  const onVenueChange = (item: Venue) => {
    if (error) resetError();
    setSelectedVenue(item);
    setForm({ ...form, venueId: item.id });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getInitialForm>) => {
    return validationMessage[key];
  };

  // FORM SUBMIT
  const onUpdateUserClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SUBMITS THE FORM TO UPDATE ADMIN
    const payload: UpdateAdminPayload = {
      id: admin.id,
      name: form.name,
      role: form.role,
      sendAlertEmails: form.sendAlertEmails,
      venueId: form.role === MAINTENANCE && selectedVenue ? selectedVenue.id : undefined,
      password: form.password ? form.password : undefined,
      email: form.email,
      pinCode: form.pinCode
    };

    updateAdmin(payload);
  };

  // CHECKS ROLE
  const isMaintenance = form.role === MAINTENANCE;

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput || hasInitialInput;

  return (
    <Grid direction="row">
      <Grid direction="column" className={classes.userFormContainer}>
        <MuiBox className={classes.updateVenueBox}>
          <Typography sx={{ fontWeight: 'bold' }} className={classes.updateVenueLabel}>
            Role
          </Typography>
          <SelectDropdown
            placeholder="Select a Role"
            items={getSortedList(roles, 'name')}
            selectedItem={selectedRole as RoleItem}
            onChange={(item: RoleItem) => handleRoleChange(item)}
            itemToString={(item: RoleItem) => (item ? item.name : '')}
          />
        </MuiBox>
        <MuiBox className={classes.updateTextInputBlock}>
          <InputField
            label="Name"
            size="medium"
            fullWidth
            placeholder="User's Name"
            value={form.name}
            onChange={(e) => onInputChange('name', e)}
            onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
            helperText={validate('name')}
            error={validation.name === INVALID}
          />
        </MuiBox>
        <MuiBox className={classes.updateTextInputBlock}>
          <InputField
            label="Email"
            size="medium"
            fullWidth
            placeholder="User's Email"
            value={form.email}
            onChange={(e) => onInputChange('email', e)}
            onBlur={(e) => onInputBlur('email', e, e.target.value.trim())}
            helperText={validate('email')}
            error={validation.email === INVALID}
          />
        </MuiBox>
        <Grid className={classes.updateUserPinCodeBlock}>
          <MuiBox className={classes.updatePinCodeBlock}>
            <InputField
              label="PIN Code"
              size="medium"
              fullWidth
              placeholder="Enter 5 Digit PIN Code"
              value={form.pinCode}
              onChange={(e) => onInputChange('pinCode', e)}
              onBlur={(e) => onInputBlur('pinCode', e, e.target.value.trim())}
              helperText={validate('pinCode')}
              error={validation.pinCode === INVALID}
            />
          </MuiBox>
          <MuiBox className={classes.updateFormPinCodeButtonBox}>
            <Button
              customSize="small"
              onClick={getValidPinCode}
              width={220}
              type="submit"
              className={classes.updateUserSaveButton}
            >
              Generate New PIN
            </Button>
          </MuiBox>
        </Grid>
        {!isMaintenance && admin.role === 'MaintenanceAdmin' && (
          <MuiBox className={classes.updateAdminPasswordBox}>
            <InputField
              label="Admin Password"
              size="medium"
              fullWidth
              placeholder="Admin Password"
              value={form.password}
              type="password"
              onChange={(e) => onInputChange('password', e)}
              onBlur={(e) => onInputBlur('password', e, e.target.value.trim())}
              onFocus={() => setPasswordInfoVisibility(true)}
              helperText={validate('password')}
              error={validation.password === INVALID}
              endIcon={<ValidPasswordToolTip showValidPasswordInfo={showPasswordInfo} value={form.password} />}
            />
          </MuiBox>
        )}
        {isMaintenance && (
          <MuiBox className={classes.updateVenueBox}>
            <Typography sx={{ fontWeight: 'bold' }} className={classes.updateVenueLabel}>
              Venue Name
            </Typography>
            <SelectDropdown
              placeholder="Select a Venue"
              items={getSortedList(venues, 'names')}
              selectedItem={selectedVenue}
              onChange={(item: Venue) => onVenueChange(item)}
              itemToString={(item: Venue) => (item ? item.name : '')}
            />
          </MuiBox>
        )}
        <Box flexOverride="none" fullWidth margin="0 1 4">
          <Card>
            <Card.ActionArea onClick={onAlertCheckboxClick}>
              <Grid className={classes.updateSendAlertBlock}>
                <Checkbox
                  checked={form.sendAlertEmails}
                  label={<Typography>Send alert emails</Typography>}
                  size="medium"
                />
              </Grid>
            </Card.ActionArea>
          </Card>
        </Box>
      </Grid>
      <Box margin="0 6 5">
        <Typography sx={{ color: color.RED }}>{error}</Typography>
      </Box>
      <MuiBox className={classes.updateUserSave}>
        <Button
          disabled={buttonDisabled}
          customSize="medium"
          onClick={onUpdateUserClick}
          width={250}
          type="submit"
          className={classes.updateUserSaveButton}
        >
          Save
        </Button>
      </MuiBox>
    </Grid>
  );
};
